/**
 * Runtime Governance Engine Page (F034 / REGE)
 * Route: /runtime-governance | Nav: Model > Runtime Decisions
 *
 * Read-only decision history for the Runtime Execution Governance Engine — every execution
 * REGE evaluated, the verdict it reached, and the signals (trust, blast radius, risk class,
 * drift, reversibility, data sensitivity) that drove the decision. Built on the List & Grid
 * Standard — see `.github/context/list-grid-standard.context.md` and `-behaviours.context.md`,
 * mirroring `src/pages/McpGovernancePage.tsx` (F032) for conventions.
 *
 * Deliberately read-only: REGE's own `evaluate` endpoint is the hot-path write, not a portal-UI
 * surface (see `runtimeGovernanceApi` in `services/api.ts`) — this page has no mutating actions,
 * no register/approve/reject affordances, just the decision list and its detail.
 *
 * Binds to `RuntimeGovernanceController` (`api/portal/runtime-governance-engine`).
 */
import { useEffect, useState } from 'react';
import type { FC, ReactNode } from 'react';
import {
  Text,
  Caption1,
  Badge,
  Button,
  Dropdown,
  Option,
  Spinner,
  Tooltip,
  Toaster,
  Toast,
  ToastTitle,
  ToastBody,
  useId,
  useToastController,
  makeStyles,
  tokens,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
} from '@fluentui/react-components';
import type { BadgeProps } from '@fluentui/react-components';
import {
  ChevronRightRegular,
  ShieldLockRegular,
  CopyRegular,
  ArrowSort16Regular,
  ArrowSortUp16Regular,
  ArrowSortDown16Regular,
} from '@fluentui/react-icons';
import { useQuery } from '@tanstack/react-query';
import { runtimeGovernanceApi } from '../services/api';
import type { ExecutionDecision, RegeVerdict } from '../types';
import {
  ListPageLayout,
  MasterDetailHeader,
  ListToolbar,
  Pager,
  actionsColumnWidth,
  useListUrlState,
  useAdaptiveGridData,
  LoadingSkeleton,
  ErrorState,
  EmptyState,
  ListLoadingOverlay,
} from '../components/list';
import type { Crumb, ListQueryParams, PagedResult, SortDirection, SortState } from '../components/list';
import {
  DetailDrawer,
  DetailSection,
  DetailField,
  DetailMetadataGrid,
  DetailBadges,
} from '../components/common/DetailDrawer';

// ---------------------------------------------------------------------------
// Enum option list — verbatim C# `RegeVerdict` enum member order (ascending restrictiveness).
// ---------------------------------------------------------------------------

const REGE_VERDICT_OPTIONS: RegeVerdict[] = [
  'Allow',
  'AllowWithConstraints',
  'ReadOnlyOnly',
  'SandboxOnly',
  'RequireApproval',
  'Escalate',
  'Block',
  'Quarantine',
  'TerminateExecution',
];

/** Ordinal rank matching the C# `RegeVerdict` enum (Allow=0 .. TerminateExecution=8) — the default
 *  sort comparator's `localeCompare` would otherwise sort verdicts alphabetically, which is not
 *  restrictiveness order. */
const VERDICT_RANK: Record<RegeVerdict, number> = {
  Allow: 0,
  AllowWithConstraints: 1,
  ReadOnlyOnly: 2,
  SandboxOnly: 3,
  RequireApproval: 4,
  Escalate: 5,
  Block: 6,
  Quarantine: 7,
  TerminateExecution: 8,
};

/** Semantic color map by restrictiveness — Allow/AllowWithConstraints read as success, the
 *  read-only/sandbox/approval/escalate band reads as warning/severe, and the hard-stop band
 *  (Block/Quarantine/TerminateExecution) reads as danger. */
const verdictColor = (verdict: RegeVerdict): BadgeProps['color'] => {
  switch (verdict) {
    case 'Allow':
    case 'AllowWithConstraints':
      return 'success';
    case 'ReadOnlyOnly':
    case 'SandboxOnly':
    case 'RequireApproval':
      return 'warning';
    case 'Escalate':
      return 'severe';
    case 'Block':
    case 'Quarantine':
    case 'TerminateExecution':
      return 'danger';
    default:
      return 'informative';
  }
};

function getDecisionSortValue(item: ExecutionDecision, field: string): unknown {
  if (field === 'verdict') return VERDICT_RANK[item.verdict];
  return (item as unknown as Record<string, unknown>)[field];
}

// ---------------------------------------------------------------------------
// Filter state — single server-side facet (verdict), encoded into the shared `filters` URL param
// (§17/§19), `field:op:value` — same convention as McpGovernancePage / AgentRegistryPage.
// ---------------------------------------------------------------------------

interface DecisionFilterState {
  verdict: RegeVerdict | '';
}

const EMPTY_DECISION_FILTERS: DecisionFilterState = { verdict: '' };

function parseDecisionFilters(raw: string): DecisionFilterState {
  const state: DecisionFilterState = { ...EMPTY_DECISION_FILTERS };
  if (!raw) return state;
  for (const part of raw.split(';')) {
    const [field, , ...rest] = part.split(':');
    const value = rest.join(':');
    if (field === 'verdict' && value) state.verdict = value as RegeVerdict;
  }
  return state;
}

function serializeDecisionFilters(state: DecisionFilterState): string {
  return state.verdict ? `verdict:eq:${state.verdict}` : '';
}

async function fetchDecisionsPage(
  verdict: RegeVerdict | '',
  params: ListQueryParams
): Promise<PagedResult<ExecutionDecision>> {
  const take = params.pageSize;
  const skip = (params.page - 1) * take;
  const result = await runtimeGovernanceApi.getDecisions(verdict || undefined, skip, take);
  const totalPages = Math.max(Math.ceil(result.totalCount / take), 1);
  return {
    items: result.items,
    totalCount: result.totalCount,
    pageNumber: params.page,
    pageSize: take,
    totalPages,
    hasPreviousPage: params.page > 1,
    hasNextPage: params.page < totalPages,
  };
}

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const useStyles = makeStyles({
  page: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    minHeight: 0,
    gap: tokens.spacingVerticalL,
  },
  listSection: {
    flexGrow: 1,
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
  },
  filterDropdown: {
    minWidth: '11rem',
  },
  lineageRow: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXS,
  },
  lineageId: {
    fontFamily: 'monospace',
  },
});

const crumbs: Crumb[] = [{ label: 'Runtime Decisions' }];

// ---------------------------------------------------------------------------
// DecisionDetailDrawer
// ---------------------------------------------------------------------------

const DecisionDetailDrawer: FC<{
  decisionId: string | null;
  onClose: () => void;
  toasterId: string;
}> = ({ decisionId, onClose, toasterId }) => {
  const styles = useStyles();
  const { dispatchToast } = useToastController(toasterId);

  const { data, isLoading, isError } = useQuery<ExecutionDecision>({
    queryKey: ['runtime-decision-detail', decisionId],
    queryFn: () => runtimeGovernanceApi.getDecision(decisionId!),
    enabled: decisionId !== null,
  });

  const handleCopyLineageId = async (id: string) => {
    try {
      await navigator.clipboard.writeText(id);
      dispatchToast(
        <Toast>
          <ToastTitle>Copied</ToastTitle>
          <ToastBody>Lineage entry ID copied to clipboard.</ToastBody>
        </Toast>,
        { intent: 'success', timeout: 3000 }
      );
    } catch {
      // Clipboard API unavailable/blocked in this context — the ID remains selectable manually.
    }
  };

  return (
    <DetailDrawer open={decisionId !== null} onClose={onClose} title="Execution Decision Detail" size="large">
      {isLoading && (
        <div style={{ display: 'flex', justifyContent: 'center', padding: tokens.spacingVerticalXXXL }}>
          <Spinner label="Loading decision…" />
        </div>
      )}
      {isError && <ErrorState message="Unable to load this decision's detail." />}
      {data && (
        <>
          <DetailSection title={`Decision ${data.decisionId}`}>
            <Badge appearance="tint" color={verdictColor(data.verdict)} size="large">
              {data.verdict}
            </Badge>
            <DetailMetadataGrid>
              <DetailField label="Reason">
                <Text>{data.reason || '—'}</Text>
              </DetailField>
              <DetailField label="Ledger Verdict">
                <Text>{data.ledgerVerdict}</Text>
              </DetailField>
              <DetailField label="Reversibility">
                <Text>{data.reversibility}</Text>
              </DetailField>
              <DetailField label="Data Sensitivity">
                <Text>{data.dataSensitivity}</Text>
              </DetailField>
              <DetailField label="Decision Latency">
                <Text>{data.decisionLatencyMs} ms</Text>
              </DetailField>
              <DetailField label="Decided At">
                <Text>{new Date(data.decidedAt).toLocaleString()}</Text>
              </DetailField>
            </DetailMetadataGrid>
          </DetailSection>

          <DetailSection title={`Constraints (${data.constraints.length})`} divider>
            {data.constraints.length === 0 ? (
              <Caption1>No constraints attached to this decision.</Caption1>
            ) : (
              <DetailBadges>
                {data.constraints.map((constraint, index) => (
                  <Badge key={`${constraint}-${index}`} appearance="outline">
                    {constraint}
                  </Badge>
                ))}
              </DetailBadges>
            )}
          </DetailSection>

          <DetailSection title={`Matched Rules (${data.matchedRules.length})`} divider>
            {data.matchedRules.length === 0 ? (
              <Caption1>No rules matched this decision.</Caption1>
            ) : (
              <DetailBadges>
                {data.matchedRules.map((rule, index) => (
                  <Badge key={`${rule}-${index}`} appearance="outline" color="informative">
                    {rule}
                  </Badge>
                ))}
              </DetailBadges>
            )}
          </DetailSection>

          <DetailSection title="Lineage" divider>
            {data.lineageEntryId ? (
              <>
                <Caption1>Lineage entry {data.lineageEntryId}</Caption1>
                <div className={styles.lineageRow}>
                  <Text className={styles.lineageId}>{data.lineageEntryId}</Text>
                  <Tooltip content="Copy lineage entry ID" relationship="label">
                    <Button
                      appearance="subtle"
                      icon={<CopyRegular />}
                      size="small"
                      aria-label="Copy lineage entry ID"
                      onClick={() => handleCopyLineageId(data.lineageEntryId!)}
                    />
                  </Tooltip>
                </div>
                <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>
                  This decision's F037 execution ledger entry. A lineage viewer is not yet
                  available — copy the ID for manual lookup.
                </Caption1>
              </>
            ) : (
              <Caption1>No lineage entry recorded for this decision.</Caption1>
            )}
          </DetailSection>
        </>
      )}
    </DetailDrawer>
  );
};

// ---------------------------------------------------------------------------
// RuntimeGovernancePage
// ---------------------------------------------------------------------------

export const RuntimeGovernancePage: FC = () => {
  const styles = useStyles();
  const toasterId = useId('runtime-governance-toaster');

  const [selectedDecisionId, setSelectedDecisionId] = useState<string | null>(null);

  const { state, setPage, setSort, setSearch, setFilters } = useListUrlState({ defaultSort: 'decidedAt:desc' });

  const filterState = parseDecisionFilters(state.filters);

  const updateVerdictFilter = (value: RegeVerdict | '') =>
    setFilters(serializeDecisionFilters({ verdict: value }));

  const [sortField, sortDirRaw] = state.sort!.split(':');
  const sortDir: SortDirection = sortDirRaw === 'desc' ? 'desc' : 'asc';
  const sortState: SortState[] = [{ field: sortField, dir: sortDir }];

  const toggleSort = (field: string) => {
    if (field === sortField) {
      setSort(`${field}:${sortDir === 'asc' ? 'desc' : 'asc'}`);
    } else {
      setSort(`${field}:asc`);
    }
  };

  const sortIcon = (field: string) => {
    if (field !== sortField) return <ArrowSort16Regular />;
    return sortDir === 'asc' ? <ArrowSortUp16Regular /> : <ArrowSortDown16Regular />;
  };

  const grid = useAdaptiveGridData<ExecutionDecision>({
    // Verdict is folded INTO queryKeyBase (not passed as `filtersSerialized`) because it is a
    // SERVER-SIDE facet on `RuntimeGovernanceController` — changing it must produce a fresh
    // probe/page fetch, not an in-memory re-filter of an already-filtered set.
    queryKeyBase: ['runtime-decisions', filterState.verdict],
    fetchPage: (params) => fetchDecisionsPage(filterState.verdict, params),
    page: state.page,
    pageSize: state.pageSize,
    search: state.search,
    // No server-side counterpart on this endpoint — search only takes effect in adaptive client
    // mode (total within the grid threshold), scanning the already server-filtered rows.
    searchFields: ['reason', 'riskClass', 'driftState'],
    sortState,
    getValue: getDecisionSortValue,
  });

  const {
    items: decisions,
    totalCount,
    totalPages,
    hasPreviousPage,
    hasNextPage,
    isLoading,
    isFetching,
    isError,
    refetch,
  } = grid;

  // Out-of-range clamp (§9) — never strand the user on an empty page after filter/search.
  useEffect(() => {
    if (!isLoading && state.page > totalPages) {
      setPage(totalPages);
    }
  }, [isLoading, state.page, totalPages, setPage]);

  const actionButtonCount = 1; // chevron detail only — this page is read-only, no row mutations.
  const columnCount = 8; // decidedAt, verdict, riskClass, trustScore, blastRadiusScore, driftState, latency, actions

  let listBody: ReactNode;
  if (isLoading) {
    listBody = <LoadingSkeleton rows={6} columns={columnCount} />;
  } else if (isError) {
    listBody = <ErrorState message="Unable to load the runtime decision history." onRetry={refetch} />;
  } else if (decisions.length === 0) {
    listBody = (
      <EmptyState
        icon={<ShieldLockRegular />}
        title="No Decisions Recorded"
        description="No execution decisions match the current filters, or REGE has not evaluated any executions yet for this tenant."
      />
    );
  } else {
    listBody = (
      <ListLoadingOverlay busy={isFetching && !isLoading}>
        <Table aria-label="Runtime execution decision history">
          <TableHeader>
            <TableRow>
              <TableHeaderCell
                sortable
                sortDirection={sortField === 'decidedAt' ? (sortDir === 'asc' ? 'ascending' : 'descending') : undefined}
                sortIcon={sortIcon('decidedAt')}
                onClick={() => toggleSort('decidedAt')}
              >
                Decided At
              </TableHeaderCell>
              <TableHeaderCell
                sortable
                sortDirection={sortField === 'verdict' ? (sortDir === 'asc' ? 'ascending' : 'descending') : undefined}
                sortIcon={sortIcon('verdict')}
                onClick={() => toggleSort('verdict')}
              >
                Verdict
              </TableHeaderCell>
              <TableHeaderCell>Risk Class</TableHeaderCell>
              <TableHeaderCell
                sortable
                sortDirection={sortField === 'trustScore' ? (sortDir === 'asc' ? 'ascending' : 'descending') : undefined}
                sortIcon={sortIcon('trustScore')}
                onClick={() => toggleSort('trustScore')}
              >
                Trust Score
              </TableHeaderCell>
              <TableHeaderCell
                sortable
                sortDirection={sortField === 'blastRadiusScore' ? (sortDir === 'asc' ? 'ascending' : 'descending') : undefined}
                sortIcon={sortIcon('blastRadiusScore')}
                onClick={() => toggleSort('blastRadiusScore')}
              >
                Blast Radius
              </TableHeaderCell>
              <TableHeaderCell>Drift State</TableHeaderCell>
              <TableHeaderCell
                sortable
                sortDirection={sortField === 'decisionLatencyMs' ? (sortDir === 'asc' ? 'ascending' : 'descending') : undefined}
                sortIcon={sortIcon('decisionLatencyMs')}
                onClick={() => toggleSort('decisionLatencyMs')}
              >
                Latency
              </TableHeaderCell>
              <TableHeaderCell style={{ width: actionsColumnWidth(actionButtonCount) }}>Actions</TableHeaderCell>
            </TableRow>
          </TableHeader>
          <TableBody>
            {decisions.map((item) => (
              <TableRow key={item.decisionId}>
                <TableCell>
                  <Text>{new Date(item.decidedAt).toLocaleString()}</Text>
                </TableCell>
                <TableCell>
                  <Badge appearance="tint" color={verdictColor(item.verdict)}>{item.verdict}</Badge>
                </TableCell>
                <TableCell>
                  <Badge appearance="outline">{item.riskClass}</Badge>
                </TableCell>
                <TableCell>
                  <Text>{item.trustScore}</Text>
                </TableCell>
                <TableCell>
                  <Text>{item.blastRadiusScore}</Text>
                </TableCell>
                <TableCell>
                  <Badge appearance="outline">{item.driftState}</Badge>
                </TableCell>
                <TableCell>
                  <Text>{item.decisionLatencyMs} ms</Text>
                </TableCell>
                <TableCell>
                  <Tooltip content="View Decision Details" relationship="label">
                    <Button
                      appearance="subtle"
                      icon={<ChevronRightRegular />}
                      size="small"
                      aria-label="View decision details"
                      onClick={() => setSelectedDecisionId(item.decisionId)}
                    />
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </ListLoadingOverlay>
    );
  }

  return (
    <div className={styles.page}>
      <Toaster toasterId={toasterId} position="top-end" />

      <div className={styles.listSection}>
        <ListPageLayout
          header={
            <MasterDetailHeader
              crumbs={crumbs}
              busy={isFetching && !isLoading}
              subtitle="The Runtime Execution Governance Engine's (REGE) recorded execution decisions and verdicts"
            />
          }
          toolbar={
            <ListToolbar
              search={state.search}
              onSearchChange={setSearch}
              searchPlaceholder="Search decisions…"
              extraFilters={
                <Dropdown
                  className={styles.filterDropdown}
                  placeholder="Verdict"
                  value={filterState.verdict}
                  onOptionSelect={(_, data) => updateVerdictFilter((data.optionValue || '') as RegeVerdict | '')}
                >
                  <Option value="">All Verdicts</Option>
                  {REGE_VERDICT_OPTIONS.map((v) => (
                    <Option key={v} value={v}>{v}</Option>
                  ))}
                </Dropdown>
              }
              onRefresh={refetch}
              isRefreshing={isFetching}
              testIdPrefix="runtime-governance"
            />
          }
          footer={
            <Pager
              page={state.page}
              totalPages={totalPages}
              hasPreviousPage={hasPreviousPage}
              hasNextPage={hasNextPage}
              totalCount={totalCount}
              onPageChange={setPage}
              testId="runtime-governance-pager"
            />
          }
        >
          {listBody}
        </ListPageLayout>
      </div>

      <DecisionDetailDrawer
        decisionId={selectedDecisionId}
        onClose={() => setSelectedDecisionId(null)}
        toasterId={toasterId}
      />
    </div>
  );
};
